import React from 'react';
import { Alert, AlertTitle, IconButton, Box, Typography, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PersonIcon from '@mui/icons-material/Person';
import useNetworkQualityAlert, { QualitySuggestion } from '../../hooks/useNetworkQualityAlert/useNetworkQualityAlert';

const AlertContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '80px',
  right: '16px',
  zIndex: 1400,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: 'fit-content',
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 160px)',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    left: '16px',
    right: '16px',
    maxWidth: 'none',
    width: 'auto',
  },
}));

const StyledAlert = styled(Alert)<{ severity: 'warning' | 'error' }>(({ severity }) => ({
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  borderRadius: '8px',
  ...(severity === 'warning' && {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffecb3',
    '& .MuiAlert-icon': {
      color: '#f57c00',
    },
  }),
  ...(severity === 'error' && {
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
    '& .MuiAlert-icon': {
      color: '#d32f2f',
    },
  }),
}));

const ActionText = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '4px',
  marginTop: '8px',
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
  '& svg': {
    fontSize: '1rem',
    marginTop: '2px',
    flexShrink: 0,
  },
}));

const ParticipantBadge = styled(Box)<{ isLocal: boolean }>(({ isLocal }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '0.7rem',
  fontWeight: 600,
  marginBottom: '4px',
  backgroundColor: isLocal ? '#e3f2fd' : '#f3e5f5',
  color: isLocal ? '#1565c0' : '#7b1fa2',
  '& svg': {
    fontSize: '0.8rem',
  },
}));

interface NetworkQualityAlertProps {
  maxAlerts?: number;
}

export default function NetworkQualityAlert({ maxAlerts = 5 }: NetworkQualityAlertProps) {
  const { activeAlerts, dismissAlert } = useNetworkQualityAlert();

  if (activeAlerts.length === 0) return null;

  const displayedAlerts = activeAlerts.slice(0, maxAlerts);

  return (
    <AlertContainer>
      {displayedAlerts.map((alert) => (
        <Collapse key={alert.id} in={true} timeout={300}>
          <StyledAlert
            severity={alert.severity}
            icon={alert.severity === 'error' ? <ErrorOutlineIcon /> : <WarningAmberIcon />}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => dismissAlert(alert.id)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <ParticipantBadge isLocal={alert.isLocal || false}>
              <PersonIcon />
              {alert.isLocal ? 'You' : 'Participant'}
            </ParticipantBadge>
            <AlertTitle sx={{ fontWeight: 600, marginTop: '4px', whiteSpace: 'nowrap' }}>{alert.title}</AlertTitle>
            <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{alert.message}</Typography>
            {alert.action && (
              <ActionText>
                <LightbulbOutlinedIcon />
                <span>{alert.action}</span>
              </ActionText>
            )}
          </StyledAlert>
        </Collapse>
      ))}
      {activeAlerts.length > maxAlerts && (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textAlign: 'center' }}>
          +{activeAlerts.length - maxAlerts} more alerts
        </Typography>
      )}
    </AlertContainer>
  );
}
